import { defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TrackByFunction } from '@angular/core';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';

import { GetAlertCategories, GetAlertsInit } from '@app/client/alerts/state/alerts-state.actions';
import { Alert, AlertSource } from '@app/client/alerts/state/alerts-state.models';
import { AlertsState } from '@app/client/alerts/state/alerts.state';
import { getActionFromSource } from '@app/client/alerts/utilities/alerts.utilities';
import { AlertCategory } from '@app/client/settings/user/modules/alert-settings/alert-settings.model';
import { ApiFilterValues } from '@app/core/models/api.models';
import { ApiListLoadMore, ApiListSort, ApiListUpdateFilterValues } from '@app/core/store/models/api-action.models';
import { ApiState } from '@app/core/store/states/api/api.state';
import { assertArray } from '@app/core/utilities/array.utilities';
import { KeyOf } from '@app/core/utilities/typescript.utilities';

export abstract class AlertsComponent {
  abstract readonly alertSource: AlertSource;

  @Dispatch()
  readonly getAlerts = (alertSource: AlertSource) => new GetAlertsInit(alertSource);

  @Dispatch()
  readonly getAlertCategories = () => new GetAlertCategories();

  @Dispatch()
  readonly sortAlerts = (alertSource: AlertSource, sortKey: KeyOf<Alert>, sortValue: number) =>
    new ApiListSort<Alert>(getActionFromSource(alertSource), { [sortKey]: sortValue });

  @Dispatch()
  readonly loadMoreAlerts = (alertSource: AlertSource) => new ApiListLoadMore(getActionFromSource(alertSource));

  @Dispatch()
  readonly updateAlertFilters = (alertSource: AlertSource, filter: ApiFilterValues<Alert>) =>
    new ApiListUpdateFilterValues(getActionFromSource(alertSource), filter);

  @Select(AlertsState.alerts)
  readonly alertSources$!: Observable<Record<AlertSource, Alert[]>>;

  @Select(AlertsState.categories)
  readonly alertCategories$!: Observable<AlertCategory[]>;

  readonly loadingAlerts$: Observable<boolean> = defer(() =>
    this.store.select(ApiState.loading(getActionFromSource(this.alertSource))),
  );

  readonly initLoadingAlerts$: Observable<boolean> = defer(() =>
    this.store.select(ApiState.firstLoad(getActionFromSource(this.alertSource))),
  );

  readonly alertSortValues$: Observable<Partial<Record<KeyOf<Alert>, number>>> = defer(() =>
    this.store.select(ApiState.sortValues(getActionFromSource(this.alertSource))),
  );

  readonly alertFilterValues$: Observable<ApiFilterValues<Alert>> = defer(() =>
    this.store.select(ApiState.filterValues(getActionFromSource(this.alertSource))),
  );

  readonly alerts$: Observable<Alert[]> = this.alertSources$.pipe(
    map((sources) => assertArray(sources?.[this.alertSource])),
  );

  readonly trackByAlert: TrackByFunction<Alert> = (_, alert) => alert.instanceId;

  protected constructor(readonly store: Store) {}

  onInit(): void {
    this.getAlerts(this.alertSource);
  }
}
